import React, { useMemo, useState } from "react";
import { Table, Popover, Checkbox, Tooltip, Tag, Select, Segmented, Space } from "antd";
import { InfoCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  colors,
  getSupplierInitials,
  formatMYR,
  calculateLowestPrices,
  calculateSummaryData,
  buildMaterialDataSource,
  MaterialInfo,
} from "./comparisonUtils";
import dayjs from "dayjs";

interface FullGridViewProps {
  suppliers: any[];
  evaluationScoreMap: Record<string, any>;
  materialNames: MaterialInfo[];
  selectedMaterials: string[];
  onMaterialSelection: (key: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  isAllSelected: boolean;
  shortlistedData: any[];
}

const FullGridView: React.FC<FullGridViewProps> = ({
  suppliers,
  evaluationScoreMap,
  materialNames,
  selectedMaterials,
  onMaterialSelection,
  onSelectAll,
  isAllSelected,
  shortlistedData,
}) => {
  const [visibleSupplierIDs, setVisibleSupplierIDs] = useState<string[]>(
    suppliers.map((s) => s.supplierID)
  );
  const [compactMode, setCompactMode] = useState(false);

  // When the filter is cleared (allowClear → empty array / undefined),
  // treat it as "show all" instead of rendering an empty grid.
  const visibleSuppliers = useMemo(() => {
    if (!visibleSupplierIDs || visibleSupplierIDs.length === 0) {
      return suppliers;
    }
    return suppliers.filter((s) => visibleSupplierIDs.includes(s.supplierID));
  }, [suppliers, visibleSupplierIDs]);

  const dataSource = useMemo(
    () => buildMaterialDataSource(materialNames, visibleSuppliers, shortlistedData),
    [materialNames, visibleSuppliers, shortlistedData]
  );

  const lowestPricePerMaterial = useMemo(
    () => calculateLowestPrices(materialNames, visibleSuppliers),
    [materialNames, visibleSuppliers]
  );

  const summaryData = useMemo(
    () => calculateSummaryData(visibleSuppliers, materialNames),
    [visibleSuppliers, materialNames]
  );

  // Filter options for supplier multi-select
  const supplierOptions = suppliers.map((s) => ({
    label: s.supplierName,
    value: s.supplierID,
  }));

  // Build columns
  const columns: ColumnsType<any> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
        />
      ),
      key: "selection",
      fixed: "left",
      width: 50,
      render: (_: any, record: any) => {
        const isDisabled = record.remainingQty === 0;
        return (
          <Tooltip title={isDisabled ? "Remaining quantity is 0" : ""}>
            <Checkbox
              checked={selectedMaterials.includes(record.key)}
              disabled={isDisabled}
              onChange={(e) =>
                onMaterialSelection(record.key, e.target.checked)
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      fixed: "left",
      width: 250,
      render: (text: string) => <b>{text}</b>,
    },
    {
      title: "Material Usage",
      dataIndex: "usage",
      key: "usage",
      fixed: "left",
      width: 150,
    },
    {
      title: "Asked Qty",
      dataIndex: "askedQty",
      key: "askedQty",
      fixed: "left",
      width: 110,
      align: "center",
      render: (qty: number, record: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <span>{qty}</span>
          <Popover
            content={
              <div style={{ maxWidth: 620, maxHeight: 350, overflow: "auto" }}>
                <div style={{ marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: "#222", fontWeight: 600 }}>Material:</span>{" "}
                  <span style={{ color: "#333" }}>{record.materialName}</span>
                </div>
                <div style={{ marginBottom: 10, fontSize: 13 }}>
                  <span style={{ color: "#222", fontWeight: 600 }}>UOM:</span>{" "}
                  <span style={{ color: "#333" }}>{record.uom}</span>
                </div>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: "#222", display: "flex", alignItems: "center", gap: 6 }}>
                  <CalendarOutlined style={{ color: "#1890ff" }} /> Production & Delivery Schedules
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", minWidth: 550 }}>
                    <thead>
                      <tr style={{ background: "#f7f8fa" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Supplier</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Production Plan</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Production Schedule</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Shipment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.map((s: any) => {
                        const li = (s.lineItems || []).find((l: any) => l.tenderMaterialId === record.tenderMaterialId);
                        if (!li) return null;
                        const plan = li.planningSchedule;
                        const prod = li.productionSchedule;
                        const ship = li.shipmentSchedule;
                        return (
                          <tr key={s.supplierID} style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <td style={{ padding: "6px 8px", fontWeight: 600, color: "#222", whiteSpace: "nowrap" }}>{s.supplierName}</td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {plan ? (
                                <>
                                  {dayjs(plan.startDate).format("DD-MMM-YY")}
                                  <span style={{ color: "#1890ff", margin: "0 3px" }}>&rarr;</span>
                                  {dayjs(plan.endDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 4, fontSize: 10, color: "#1890ff", fontWeight: 500 }}>({plan.leadDays}d)</span>
                                </>
                              ) : "-"}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {prod ? (
                                <>
                                  {dayjs(prod.plannedStartDate).format("DD-MMM-YY")}
                                  <span style={{ color: "#1890ff", margin: "0 3px" }}>&rarr;</span>
                                  {dayjs(prod.plannedEndDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 4, fontSize: 10, color: "#52c41a", fontWeight: 500 }}>({prod.quantity} units)</span>
                                </>
                              ) : "-"}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {ship ? (
                                <>
                                  {dayjs(ship.shippingDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 6, color: "#fa541c", fontWeight: 600, fontSize: 11 }}>ETA: {dayjs(ship.eta).format("DD-MMM-YY")}</span>
                                </>
                              ) : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            }
            title={null}
            placement="bottom"
            overlayStyle={{ maxWidth: 650 }}
            overlayInnerStyle={{ padding: 12 }}
            trigger="hover"
          >
            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
          </Popover>
        </div>
      ),
    },
    {
      title: "Need By Date",
      dataIndex: "needByDate",
      key: "needByDate",
      fixed: "left",
      width: 120,
      align: "center",
      render: (date: string) => date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
  ];

  // Dynamic supplier columns
  visibleSuppliers.forEach((supplier) => {
    const globalIndex = suppliers.findIndex(
      (s) => s.supplierID === supplier.supplierID
    );
    const evalData = evaluationScoreMap[supplier.supplierID];

    const supplierHeader = (
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Popover
            content={
              <div>
                <div><b>Supplier:</b> {supplier.supplierName}</div>
                <div><b>ID:</b> {supplier.supplierID}</div>
                <div><b>Payment Terms:</b> {supplier.paymentTerms}</div>
                <div><b>Quote Valid:</b> {supplier.quoteValidityDate}</div>
                {evalData && (
                  <>
                    <hr style={{ margin: "6px 0" }} />
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Evaluation Scores
                    </div>
                    {evalData.sections.map((s: any) => (
                      <div
                        key={s.name}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <span>{s.name}:</span>
                        <span>{s.score}/{s.maxScore}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            }
            title="Supplier Info"
            placement="top"
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: colors[globalIndex % colors.length],
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {getSupplierInitials(supplier.supplierName)}
            </div>
          </Popover>
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          {supplier.supplierName}
        </div>
        {evalData && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 4,
              marginTop: 4,
              flexWrap: "wrap",
            }}
          >
            <Tag
              color={
                evalData.rank === 1
                  ? "gold"
                  : evalData.rank === 2
                  ? "default"
                  : evalData.rank === 3
                  ? "orange"
                  : "blue"
              }
              style={{ fontSize: 11, margin: 0 }}
            >
              Rank #{evalData.rank}
            </Tag>
            <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
              Score: {evalData.overallScore.toFixed(1)}
            </Tag>
          </div>
        )}
      </div>
    );

    if (compactMode) {
      // Compact: only Total Price column
      columns.push({
        title: supplierHeader,
        dataIndex: supplier.supplierID,
        key: supplier.supplierID,
        width: 150,
        align: "right" as const,
        render: (val: any, record: any) => {
          if (!val) return "-";
          const isLowest =
            val.totalPrice === lowestPricePerMaterial[record.tenderMaterialId];
          return (
            <span
              style={{
                color: isLowest ? "#52c41a" : "inherit",
                fontWeight: isLowest ? 600 : 400,
              }}
            >
              {formatMYR(val.totalPrice)}
            </span>
          );
        },
      });
    } else {
      // Expanded: 3 sub-columns
      columns.push({
        title: supplierHeader,
        key: supplier.supplierID,
        children: [
          {
            title: "Supply Qty",
            dataIndex: supplier.supplierID,
            key: `${supplier.supplierID}_qty`,
            width: 100,
            align: "center" as const,
            render: (val: any) => (val ? val.supplyQty : "-"),
          },
          {
            title: "Unit Price (INR)",
            dataIndex: supplier.supplierID,
            key: `${supplier.supplierID}_unit`,
            width: 130,
            align: "right" as const,
            render: (val: any) => (val ? formatMYR(val.unitPrice) : "-"),
          },
          {
            title: "Total Price (INR)",
            dataIndex: supplier.supplierID,
            key: `${supplier.supplierID}_total`,
            width: 140,
            align: "right" as const,
            render: (val: any, record: any) => {
              if (!val) return "-";
              const isLowest =
                val.totalPrice === lowestPricePerMaterial[record.tenderMaterialId];
              return (
                <span
                  style={{
                    color: isLowest ? "#52c41a" : "inherit",
                    fontWeight: isLowest ? 600 : 400,
                  }}
                >
                  {formatMYR(val.totalPrice)}
                </span>
              );
            },
          },
        ],
      });
    }
  });

  // Summary rendering helpers
  const renderSummaryCells = (
    getValue: (data: any) => React.ReactNode,
    align: "center" | "right" = "right"
  ) =>
    visibleSuppliers.map((supplier, idx) => {
      const data = summaryData.find(
        (s) => s.supplierID === supplier.supplierID
      );
      if (compactMode) {
        return (
          <Table.Summary.Cell
            key={`${supplier.supplierID}`}
            index={5 + idx}
            align={align}
          >
            {data ? getValue(data) : "-"}
          </Table.Summary.Cell>
        );
      }
      return (
        <React.Fragment key={supplier.supplierID}>
          <Table.Summary.Cell index={5 + idx * 3} align="center">
            -
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6 + idx * 3} align="right">
            -
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7 + idx * 3} align={align}>
            {data ? getValue(data) : "-"}
          </Table.Summary.Cell>
        </React.Fragment>
      );
    });

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space wrap>
          <Select
            mode="multiple"
            placeholder="Filter suppliers..."
            maxTagCount={3}
            options={supplierOptions}
            value={visibleSupplierIDs}
            onChange={(vals) => setVisibleSupplierIDs(vals ?? [])}
            style={{ minWidth: 350 }}
            allowClear
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Space>
        <Segmented
          options={[
            { label: "Expanded", value: "expanded" },
            { label: "Compact", value: "compact" },
          ]}
          value={compactMode ? "compact" : "expanded"}
          onChange={(val) => setCompactMode(val === "compact")}
        />
      </div>

      {/* Table */}
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
        summary={() => (
          <Table.Summary fixed>
            {/* Sub Total */}
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <b>Sub Total</b>
              </Table.Summary.Cell>
              {renderSummaryCells((data) => (
                <b>{formatMYR(data.subTotal)}</b>
              ))}
            </Table.Summary.Row>

            {/* Taxes */}
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <b>Taxes (SST 6%)</b>
              </Table.Summary.Cell>
              {renderSummaryCells((data) => formatMYR(data.taxes))}
            </Table.Summary.Row>

            {/* Grand Total */}
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <b style={{ color: "#1890ff", fontSize: 14 }}>Grand Total</b>
              </Table.Summary.Cell>
              {renderSummaryCells((data) => (
                <b style={{ color: "#1890ff", fontSize: 14 }}>
                  {formatMYR(data.grandTotal)}
                </b>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default FullGridView;
